<template>
  <b-card no-body class="border mb-2">
    <b-card-header class="pb-1">
      <div>
        <b-card-title class="mb-25">Data scope</b-card-title>
        <b-card-text class="text-muted small mb-0">
          Which records this client may reach, per resource. Location grants
          <strong>inherit downward</strong> — pick one Area and the client reaches every
          district, assembly and member under it; pick one RMC and it reaches every
          country and member under that. There is no need to list the children.
          Dimensions combine with <strong>AND</strong>, so a client restricted to one area
          and one member type sees the intersection. Every dimension needs a rule: a
          missing one denies access. Writes are held to the same grant — a new record has
          to land inside it.
        </b-card-text>
      </div>
    </b-card-header>

    <b-card-body>
      <b-alert v-if="!scopedResources.length" variant="secondary" show class="mb-0">
        <div class="alert-body">
          Grant at least one permission above to configure its data scope.
        </div>
      </b-alert>

      <div v-else>
        <div
          v-for="resource in scopedResources"
          :key="resource.resource"
          class="mb-2 pb-1 border-bottom"
        >
          <div class="d-flex align-items-center justify-content-between mb-1">
            <h5 class="mb-0">{{ humanize(resource.resource) }}</h5>

            <b-button
              v-if="unsetDimensions(resource).length"
              variant="flat-primary"
              size="sm"
              :disabled="disabled"
              @click="allowRemainingDimensions(resource)"
            >
              Set the other {{ unsetDimensions(resource).length }}
              {{ unsetDimensions(resource).length === 1 ? 'dimension' : 'dimensions' }} to All
            </b-button>
          </div>

          <div
            v-for="dimension in resource.dimensions"
            :key="`${resource.resource}-${dimension.key}`"
            class="mb-1"
          >
            <b-row>
              <b-col cols="12" md="4">
                <div class="font-weight-bold">{{ humanize(dimension.key) }}</div>
                <small class="text-muted">
                  {{ dimension.matcher === 'location_tree' ? 'Location hierarchy' : dimension.field }}
                </small>
              </b-col>

              <b-col cols="12" md="8">
                <b-form-radio-group
                  :checked="modeFor(resource.resource, dimension.key)"
                  :disabled="disabled"
                  buttons
                  button-variant="outline-primary"
                  size="sm"
                  class="mb-1"
                  @change="setMode(resource.resource, dimension.key, $event)"
                >
                  <b-form-radio value="all">All</b-form-radio>
                  <b-form-radio value="restricted">Restricted</b-form-radio>
                </b-form-radio-group>

                <div v-if="modeFor(resource.resource, dimension.key) === 'restricted'">
                  <template v-if="dimension.matcher === 'location_tree'">
                    <location-grant-picker
                      :value="valuesFor(resource.resource, dimension.key)"
                      :disabled="disabled"
                      @input="setValues(resource.resource, dimension.key, $event)"
                    />

                    <b-button
                      v-if="otherLocationResources(resource.resource).length"
                      variant="flat-primary"
                      size="sm"
                      class="mt-50 px-0"
                      :disabled="disabled || !valuesFor(resource.resource, dimension.key).length"
                      @click="applyLocationEverywhere(resource.resource, dimension.key)"
                    >
                      Copy this location to the other
                      {{ otherLocationResources(resource.resource).length }}
                      {{ otherLocationResources(resource.resource).length === 1 ? 'resource' : 'resources' }}
                    </b-button>
                  </template>

                  <v-select
                    v-else
                    multiple
                    :value="valuesFor(resource.resource, dimension.key)"
                    :options="optionsFor(dimension.field)"
                    :reduce="option => option.value"
                    :loading="loadingFields.includes(dimension.field)"
                    :disabled="disabled"
                    placeholder="Select the permitted values"
                    @input="setValues(resource.resource, dimension.key, $event || [])"
                  />

                  <small
                    v-if="dimension.field && !loadingFields.includes(dimension.field) && !optionsFor(dimension.field).length"
                    class="text-danger"
                  >
                    No options could be loaded for {{ dimension.field }}.
                  </small>

                  <b-alert
                    v-if="!valuesFor(resource.resource, dimension.key).length"
                    variant="warning"
                    show
                    class="mt-50 mb-0"
                  >
                    <div class="alert-body small">
                      Restricted with no values selected — this denies all access to
                      {{ humanize(resource.resource) }}.
                    </div>
                  </b-alert>

                  <!--
                    A restricted grant confines writes to the granted subtree. On a
                    resource that has no parent at all, that means creates are
                    impossible — worth saying here rather than letting the partner
                    discover it as a 403.
                  -->
                  <b-alert
                    v-else-if="
                      dimension.matcher === 'location_tree'
                        && isRootResource(resource)
                        && grantsCreate(resource.resource)
                    "
                    variant="warning"
                    show
                    class="mt-50 mb-0"
                  >
                    <div class="alert-body small">
                      {{ humanize(resource.resource) }} sits at the top of its hierarchy,
                      so a restricted grant leaves nothing for a new record to belong to:
                      <strong>Create</strong> will be refused for this client. Set this
                      dimension to <strong>All</strong> if it genuinely needs to add
                      {{ humanize(resource.resource).toLowerCase() }}.
                    </div>
                  </b-alert>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { get } from 'lodash'
import vSelect from 'vue-select'
import {
  BRow,
  BCol,
  BCard,
  BAlert,
  BButton,
  BCardBody,
  BCardText,
  BCardTitle,
  BCardHeader,
  BFormRadio,
  BFormRadioGroup,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import LocationGrantPicker from './LocationGrantPicker.vue'
import unwrapList from './unwrapList'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BAlert,
    BButton,
    BCardBody,
    BCardText,
    BCardTitle,
    BCardHeader,
    BFormRadio,
    BFormRadioGroup,
    vSelect,
    LocationGrantPicker,
  },
  props: {
    // [{ resource, dimension_key, mode, values: [] }]
    value: {
      type: Array,
      default: () => [],
    },
    // registry entries
    resources: {
      type: Array,
      default: () => [],
    },
    // current permissions, so we only ask about resources the client can actually reach
    permissions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingFields: [],
      optionsByField: {},
    }
  },
  computed: {
    scopedResources() {
      const granted = this.permissions.map(permission => permission.resource)

      return this.resources.filter(
        resource => granted.includes(resource.resource) && resource.dimensions.length
      )
    },
  },
  watch: {
    scopedResources: {
      handler(resources) {
        resources.forEach(resource => {
          resource.dimensions
            .filter(dimension => dimension.matcher === 'field_in' && dimension.field)
            .forEach(dimension => this.loadFieldOptions(dimension.field))
        })
      },
      immediate: true,
    },
  },
  methods: {
    /** No parent to hang a new record off — a region, or a mission block. */
    isRootResource(resource) {
      return Boolean(resource.soft_delete) && !(resource.parent_fields || []).length
    },
    grantsCreate(resource_name) {
      const granted = this.permissions.find(entry => entry.resource === resource_name)
      return Boolean(granted && (granted.actions || []).includes('CanAddNew'))
    },
    humanize(value) {
      return String(value)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    ruleFor(resource, dimension_key) {
      return this.value.find(
        rule => rule.resource === resource && rule.dimension_key === dimension_key
      )
    },
    modeFor(resource, dimension_key) {
      const rule = this.ruleFor(resource, dimension_key)
      // Default the control to "restricted" so an admin has to consciously widen it.
      return rule ? rule.mode : 'restricted'
    },
    valuesFor(resource, dimension_key) {
      const rule = this.ruleFor(resource, dimension_key)
      return rule && Array.isArray(rule.values) ? rule.values.map(String) : []
    },
    /**
     * All rule edits funnel through here so a bulk action emits one `input` rather than
     * one per rule — with a v-model array, successive emits inside the same tick would
     * each rebuild from a stale `this.value` and drop all but the last change.
     */
    applyPatches(patches) {
      const next = this.value.map(rule => ({ ...rule, values: [...(rule.values || [])] }))

      patches.forEach(patch => {
        const existing = next.find(
          rule => rule.resource === patch.resource && rule.dimension_key === patch.dimension_key
        )

        if (existing) {
          Object.assign(existing, patch)
        } else {
          next.push({ mode: 'restricted', values: [], ...patch })
        }
      })

      this.$emit('input', next)
    },
    upsert(resource, dimension_key, patch) {
      this.applyPatches([{ resource, dimension_key, ...patch }])
    },
    setMode(resource, dimension_key, mode) {
      this.upsert(resource, dimension_key, { mode })
    },
    setValues(resource, dimension_key, values) {
      this.upsert(resource, dimension_key, { values: values || [] })
    },
    /** Dimensions with no saved rule — the ones silently denying access right now. */
    unsetDimensions(resource) {
      return resource.dimensions.filter(
        dimension => !this.ruleFor(resource.resource, dimension.key)
      )
    },
    allowRemainingDimensions(resource) {
      const patches = this.unsetDimensions(resource).map(dimension => ({
        resource: resource.resource,
        dimension_key: dimension.key,
        mode: 'all',
        values: [],
      }))

      if (!patches.length) return

      this.applyPatches(patches)
      this.notify(
        `${patches.length} ${patches.length === 1 ? 'dimension' : 'dimensions'} on ` +
          `${this.humanize(resource.resource)} set to All.`
      )
    },
    /** Other granted resources that also scope on location — the copy targets. */
    otherLocationResources(resource) {
      return this.scopedResources.filter(candidate => {
        if (candidate.resource === resource) return false
        return candidate.dimensions.some(dimension => dimension.matcher === 'location_tree')
      })
    },
    /**
     * A location grant is per resource, so scoping a client to one Area otherwise means
     * re-picking that area on members, districts, assemblies and so on. The grant means
     * the same thing everywhere, so copying it is safe and is the common case.
     */
    applyLocationEverywhere(resource, dimension_key) {
      const source = this.ruleFor(resource, dimension_key)
      if (!source) return

      const targets = this.otherLocationResources(resource)
      if (!targets.length) return

      this.applyPatches(
        targets.map(target => ({
          resource: target.resource,
          dimension_key: target.dimensions.find(
            dimension => dimension.matcher === 'location_tree'
          ).key,
          mode: source.mode,
          values: [...(source.values || [])],
        }))
      )

      this.notify(
        `Location copied to ${targets.length} other ` +
          `${targets.length === 1 ? 'resource' : 'resources'}.`
      )
    },
    notify(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Scope updated',
          icon: 'CheckCircleIcon',
          variant: 'success',
          text,
        },
      })
    },
    optionsFor(field) {
      return this.optionsByField[field] || []
    },
    async loadFieldOptions(field) {
      // An empty array is truthy, so caching on presence alone would make one failed or
      // empty fetch blank this picker permanently. Only a non-empty result counts as
      // cached; anything else is retried on the next dependency change.
      const cached = this.optionsByField[field]
      if ((cached && cached.length) || this.loadingFields.includes(field)) return

      try {
        this.loadingFields.push(field)

        const rows = await this.fetchField(field)
        const options = rows.map(row => ({
          value: row._id,
          label: row.title || row.designation || row.name || row._id,
        }))

        this.$set(this.optionsByField, field, options)
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loadingFields = this.loadingFields.filter(entry => entry !== field)
      }
    },
    /**
     * These read the admin list endpoints rather than the `/shared/*` ones.
     *
     * `/shared/member-designations` and `/shared/member-titles` unconditionally run
     * `new Types.ObjectId(member_type_id)` on a param that defaults to `""`, which
     * throws and 500s — they exist to populate a cascading select once a member type is
     * chosen, not to list everything. The admin endpoints list all rows, take a plain
     * limit, and return a consistent envelope.
     */
    async fetchField(field) {
      const service = this.useJwt().mainService
      const unwrap = unwrapList
      // These are reference collections in the tens to low hundreds; an admin picking
      // scope values needs all of them, not the default first page of 10.
      const params = { limit: 1000, page: 1 }

      switch (field) {
        case 'member_type_id':
          return unwrap(await service.memberTypes.list(params))
        case 'member_status_id':
          return unwrap(await service.memberStatus.list(params))
        case 'member_designation_id':
          return unwrap(await service.memberDesignations.list(params))
        case 'member_title_id':
          return unwrap(await service.memberTitles.list(params))
        case 'department_id':
          return unwrap(await service.departments.list(params))
        default:
          return []
      }
    },
  },
}
</script>
